import * as React from 'react';
import {
  Button,
  Icon,
  SubTitleSmall,
  BodyMedium,
} from '@spaced-out/ui-design-system';
import type {ColorTypes} from '@spaced-out/ui-design-system/lib/types/typography';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {
  ActionCardWidgetPayload,
  ActionEvent,
} from 'src/components/agentic-ui/types';
import css from './action-card-widget.css';


interface ActionCardWidgetProps {
  payload: ActionCardWidgetPayload;
  publishMessageAction?: (action: ActionEvent) => void;
  /** Dock chrome handlers (provided when rendered in the sub-chat dock). */
  onMinimize: () => void;
  onClose: () => void;
  /** Disable the footer actions while the server is responding. */
  disabled?: boolean;
}

export function ActionCardWidget({
  payload,
  publishMessageAction,
  onMinimize,
  onClose,
  disabled = false,
}: ActionCardWidgetProps): React.ReactNode {
  const {t} = useLabelTranslation();
  const {props, positiveEvent} = payload ?? {};

  if (!props?.title) {
    return null;
  }

  // Positive (primary) CTA: publish the backend-provided event so the grace
  // event handlers route the UI. Never touches the backend chat directly.
  const handlePositive = (): void => {
    if (!positiveEvent) {
      return;
    }
    const {product, name, payload: eventPayload} = positiveEvent;
    publishMessageAction?.({product, event: name, payload: eventPayload});
  };

  // Negative (secondary) CTA: publish the UI-driven event handled in
  // useHandleAskAiEvents, which returns the user to normal chat.
  const handleNegative = (): void => {
    publishMessageAction?.({
      product: 'graceai',
      event: 'graceai.continueconversation',
      payload: {},
    });
  };

  return (
    <div className={css.card}>
      <header className={css.header}>
        <SubTitleSmall>{props.title}</SubTitleSmall>
        <div className={css.headerControls}>
          <Button
            type="ghost"
            iconLeftName="minus"
            size="small"
            onClick={onMinimize}
            aria-label={t('Minimize', 'Minimize')}
          />
          <Button
            type="ghost"
            iconLeftName="close"
            size="small"
            onClick={onClose}
            aria-label={t('Close', 'Close')}
          />
        </div>
      </header>

      <div className={css.body}>
        <Icon
          name={props.iconName ?? 'circle-check'}
          color={(props.iconColor as ColorTypes | undefined) ?? 'information'}
          type="regular"
          size="medium"
        />
        {props.subtitle ? (
          <BodyMedium color="primary">{props.subtitle}</BodyMedium>
        ) : null}
      </div>

      <footer className={css.footer}>
        <div className={css.footerSpacer} />
        <Button
          type="secondary"
          size="small"
          onClick={handleNegative}
          disabled={disabled}
        >
          {props.negativeLabel ??
            t('Continue_Conversation', 'Continue Conversation')}
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={handlePositive}
          disabled={disabled}
        >
          {props.positiveLabel ?? t('Go_to_Agent', 'Go to Agent')}
        </Button>
      </footer>
    </div>
  );
}
